/* eslint-disable max-len */
import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import Box from '@mui/material/Box'
import {
    DataGridPro,
    GridCallbackDetails,
    GridDataSourceCacheDefault,
    GridFilterModel,
    GridLogicOperator,
    useGridApiRef,
    GridRowSelectionModel,
} from '@mui/x-data-grid-pro'
import clsx from 'clsx'

import SkyNetPagination, {
    SkyNetPaginationMode,
} from 'app/shared-components/SkyNetPagination'
import usePaginationStyles from 'app/tss/pagination.styles'
import {
    TableControlTypes,
} from 'app/shared-components/TableControls'
import ShowArchived from 'app/shared-components/TableControls/ShowArchived'
import {
    ActionComponentsKey,
} from '../../SkyNetDomainV2/SkyNetDomain.types'
import useDataGridStyles from './SkyNetDataGrid.styles'
import {
    filterColumns, getColumnForNewFilter,
} from './components/FilterIcon/filterMenuProps'
import NoRowsErrorOverlay from './components/NoRowsErrorOverlay'
import {
    ColumnConfigMappingType, ExtendedGridFilterModel, SkyNetDataSourceProvider,
} from './SkyNetDataGrid.types'
import useDataGridColumns from './hooks/useDataGridColumns'
import SkyNetDataGridToolbar from './components/SkyNetDataGridToolbar'
import FilterIcon from './components/FilterIcon'
import ColumnMenuWithoutFilter from './components/ColumnMenuWithoutFilter'
import {
    getPredefinedDataFilter,
    getVisibleAndToggableColumns,
    resetFilterModelOnRemoveAll,
} from './utils/FilterModel.utils'
import useVisibleColumns from './hooks/useVisibleColumns'

const DELAY_TIMEOUT = 600

type SkyNetDataGridProps = {
    columns: ColumnConfigMappingType[],
    filtersUnrelatedToColumns?: ColumnConfigMappingType[],
    customDataSource: SkyNetDataSourceProvider,
    apiRef?: any,
    rowHeight?: number | 'auto',
    height?: string | number,
    toolbarControls?: TableControlTypes[],
    tabsComponent: JSX.Element,
    onRowClick?: (params: any) => void,
    rowSelectionModel?: GridRowSelectionModel,
    onRowSelectionModelChange?: (newRowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails) => void,
    isDetailsView?: boolean,
    tableWidthPercent?: number,
    initialTableWidthPercent?: number,
    toolbarActions?: ActionComponentsKey[],
}

const SkyNetDataGrid = ({
    columns: defColumns,
    filtersUnrelatedToColumns,
    customDataSource,
    rowHeight = 'auto',
    height,
    toolbarControls,
    tabsComponent,
    onRowClick,
    rowSelectionModel,
    onRowSelectionModelChange,
    isDetailsView,
    tableWidthPercent,
    initialTableWidthPercent,
    toolbarActions,
}: Readonly<SkyNetDataGridProps>) => {
    const apiRef = useGridApiRef()
    const {
        dataSource,
        onDataSourceError,
        errorMessage,
        paginationOptions,
        key,
    } = customDataSource

    const {
        classes: paginationStyles,
    } = usePaginationStyles()
    const {
        classes: dataGridClasses,
    } = useDataGridStyles({
        isDetailsView,
    })
    // this is used to set the position of filter panel and column menu when in details view
    const [
        panelTargetEl,
        setPanelTargetEl,
    ] = useState<HTMLButtonElement | null>(null)

    const lowTTLCache = new GridDataSourceCacheDefault({
        ttl: 1000 * 100,
    })
    const columnsWithLabels = useDataGridColumns({
        columnsDef: defColumns, filtersUnrelatedToColumns, customDataSource,
    })
    const getRowHeight = useCallback(() => {
        return rowHeight
    }, [rowHeight])

    const goToPage = useCallback((e, page: number) => {
        apiRef?.current?.setPage(page - 1)
        paginationOptions?.onChange(null, page)
    }, [
        apiRef,
        paginationOptions,
    ])

    const resetToFirstPage = useCallback(() => {
        goToPage(null, 1)
        // Please leave this as empty dep array to avoid re-rendering bugs of filter panel
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const filterModelInitState = useMemo(() => {
        return {
            items: getPredefinedDataFilter(columnsWithLabels),
        }
    }, [columnsWithLabels])

    const [
        localFilterModel,
        setLocalFilterModel,
    ] = useState<GridFilterModel>(filterModelInitState)

    const onFilterModelChange = useCallback((model: ExtendedGridFilterModel, details: GridCallbackDetails<'filter'>) => {
        setLocalFilterModel(resetFilterModelOnRemoveAll(model, details))
        resetToFirstPage()
    }, [resetToFirstPage])

    const {
        visibilityModel,
        onColumnVisibilityModelChange,
    } = useVisibleColumns({
        columns: columnsWithLabels,
        isDetailsView,
        tableWidthPercent,
        initialTableWidthPercent,
    })

    const activeToolbarControls = useMemo(() => {
        const controlsMap: Partial<Record<TableControlTypes, JSX.Element>> = {
            [TableControlTypes.ShowArhived]: (
                <ShowArchived
                    name={key}
                    onChange={resetToFirstPage}
                    className={{
                        iconWrapper: dataGridClasses.archiveIconWrapper,
                        link: dataGridClasses.archiveLink,
                    }}
                    embeddedInDropdown={isDetailsView}
                />
            ),
        }

        return Object.entries(controlsMap).reduce((acc, [
            controlType,
            element,
        ]) => {
            if (toolbarControls?.includes(controlType as TableControlTypes)) {
                acc.push(element)
            }
            return acc
        }, [] as JSX.Element[])
    }, [
        key,
        resetToFirstPage,
        dataGridClasses,
        isDetailsView,
        toolbarControls,
    ])

    /* this is to avoid re-rendering bugs of filter panel when tabsComponent changes
    useRef does not trigger the re-mount of toolbar with filter panel but keeps the tabsComponent updated */
    const tabsComponentRef = useRef<JSX.Element | null>(null)

    useEffect(() => {
        tabsComponentRef.current = tabsComponent
    }, [tabsComponent])

    const CustomToolbar = useCallback(() => {
        const filterableColumns = columnsWithLabels.filter((col) => {
            return col.filterable
        }) || []

        return (
            <SkyNetDataGridToolbar
                toolbarControls={activeToolbarControls}
                showFilterButton={Boolean(filterableColumns.length) || Boolean(filtersUnrelatedToColumns?.length)}
                tabsComponent={tabsComponentRef.current}
                isDetailsView={isDetailsView}
                setPanelTargetEl={setPanelTargetEl}
                toolbarActions={toolbarActions || []}
            />
        )
        // If columnsWithLabels or tabsComponent is in dep array, then we will have re-rendering bugs of filter panel
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        toolbarActions,
        isDetailsView,
        activeToolbarControls,
    ])

    const noRowsAndErrorOverlay = useCallback(() => {
        return (
            <NoRowsErrorOverlay
                error={errorMessage}
            />
        )
    }, [errorMessage])

    const Pagination = useMemo(() => {
        if (!paginationOptions?.paginated) return null

        return (
            <div className={paginationStyles.paginationContainer}>
                <SkyNetPagination
                    total={paginationOptions?.matches}
                    rowsPerPage={paginationOptions?.rows}
                    page={paginationOptions?.page}
                    onChange={goToPage}
                    mode={isDetailsView ? SkyNetPaginationMode.SIMPLE : undefined}
                    useCard={isDetailsView ? false : undefined}
                />
            </div>
        )
    }, [
        goToPage,
        isDetailsView,
        paginationOptions,
        paginationStyles,
    ])

    return (
        <>
            <Box
                className={clsx(dataGridClasses.dataGridContainer,
                    Boolean(paginationOptions?.paginated) && !isDetailsView && dataGridClasses.withPagination)}
                sx={{
                    height,
                }}
            >
                <DataGridPro
                    columns={columnsWithLabels}
                    rowHeight={36}
                    showToolbar
                    hideFooter
                    hideFooterPagination
                    dataSource={dataSource}
                    onDataSourceError={onDataSourceError}
                    dataSourceCache={lowTTLCache}
                    apiRef={apiRef}
                    density="compact"
                    pagination
                    slots={{
                        noRowsOverlay: noRowsAndErrorOverlay,
                        columnHeaderFilterIconButton: FilterIcon,
                        columnMenu: ColumnMenuWithoutFilter,
                        toolbar: CustomToolbar,
                        filterPanelRemoveAllIcon: () => {
                            return null
                        },
                        // Show pagination in details view at the bottom of the grid
                        bottomContainer: () => {
                            if (!isDetailsView) return null
                            return (
                                <div
                                    className={dataGridClasses.bottomContainer}
                                    data-testid="bottom-container"
                                >
                                    {Pagination}
                                </div>
                            )
                        },

                    }}
                    slotProps={{
                        panel: isDetailsView ? {
                            target: panelTargetEl,
                        } : undefined,
                        toolbar: {
                            quickFilterProps: {
                                debounceMs: DELAY_TIMEOUT,
                            },
                        },
                        loadingOverlay: {
                            variant: 'skeleton',
                            noRowsVariant: 'skeleton',
                        },
                        filterPanel: {
                            logicOperators: [GridLogicOperator.And],
                            // @ts-expect-error - className is missing in props type but is passed correctly and works
                            className: dataGridClasses.filterPanel,
                            filterFormProps: {
                                filterColumns,
                                logicOperatorInputProps: {
                                    className: dataGridClasses.filterPanelHiddenElement,
                                },
                                operatorInputProps: {
                                    readOnly: true,
                                    className: dataGridClasses.filterPanelHiddenElement,
                                },
                                columnInputProps: {
                                    variant: 'standard',
                                    className: dataGridClasses.filterPanelColumnInput,
                                },
                                deleteIconProps: {
                                    className: dataGridClasses.filterPanelDeleteIcon,
                                },
                            },
                            getColumnForNewFilter,
                        },
                        columnsManagement: {
                            getTogglableColumns: getVisibleAndToggableColumns,
                        },
                    }}
                    localeText={{
                        filterPanelRemoveAll: 'Clear all filters',
                        filterPanelColumns: 'Column',
                    }}
                    paginationModel={{
                        pageSize: paginationOptions?.rows || 25,
                        page: paginationOptions?.page - 1 || 0,
                    }}
                    getRowHeight={getRowHeight}
                    onSortModelChange={resetToFirstPage}
                    onFilterModelChange={onFilterModelChange}
                    columnVisibilityModel={visibilityModel}
                    onColumnVisibilityModelChange={(model) => {
                        onColumnVisibilityModelChange(model, true)
                    }}
                    filterModel={localFilterModel}
                    onRowClick={onRowClick}
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                />
            </Box>
            {!isDetailsView && Pagination}
        </>
    )
}

export default SkyNetDataGrid
